import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import DashboardWrapper from "../../components/DashboardWrapper";
import CrudTable from "../../components/dashboard/table/CrudTable";
import CrudForm from "../../components/dashboard/form/CrudForm";
import { useCrud } from "../../hooks/useCrud";
import Swal from "sweetalert2";
import { validateImageSize, validateName, validateSeoDescription, validateSeoKeywords, validateSeoTitle } from "../../utils/Validations";

const generateSlug = (text) =>
  text.toString().toLowerCase().trim().replace(/\s+/g, "-");

export default function Category() {
  const navigate = useNavigate();

  const {
    data: categories,
    loading,
    errors,
    createItem,
    updateItem,
    deleteItem,
  } = useCrud(`${process.env.REACT_APP_API_URL}/backend/categories`, [
    "cat_image",
  ]);

  const [editingItem, setEditingItem] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  // ✅ API BASED FIELDS
  const allowedFields = useMemo(
    () => [
      "name",
      "cat_image",
      "status",
      "seo_title",
      "seo_description",
      "seo_keywords",
      "description",
    ],
    []
  );

  const getFormData = useCallback(
    (item) => {
      if (!item)
        return {
          name: "",
          cat_image: null,
          status: 1,
          seo_title: "",
          seo_description: "",
          seo_keywords: "",
          description: "",
        };

      return allowedFields.reduce((acc, field) => {
        acc[field] =
          field === "status"
            ? Number(item[field] ?? 1)
            : field === "cat_image"
              ? item[field] || null
              : item[field] || "";
        return acc;
      }, {});
    },
    [allowedFields]
  );

  const hasDuplicate = useCallback(
    (name, slug, id = null) =>
      categories.some(
        (cat) =>
          (cat.name.toLowerCase() === name.toLowerCase() ||
            cat.slug.toLowerCase() === slug.toLowerCase()) &&
          cat.id !== id
      ),
    [categories]
  );

  const showSwal = useCallback((title, text, icon) => {
  Swal.fire({
    title,
    text,
    icon,
    confirmButtonText: "OK",
    customClass: {
      confirmButton: "btn btn-primary",
      popup: "category-swal-popup",
    },
    buttonsStyling: false,
    didOpen: () => {
      const popup = document.querySelector(".category-swal-popup");

      if (popup) {
        popup.style.zIndex = "20000";
      }
    },
  });
}, []);

  const handleSubmit = useCallback(
    async (formData) => {
     const nameError = validateName(formData.name);

if (nameError !== true) {
  showSwal("Validation Error!", nameError, "warning");
  return;
}

const imageError = validateImageSize(formData.cat_image);

if (imageError !== true) {
  showSwal("Validation Error!", imageError, "warning");
  return;
}

const seoTitleError = validateSeoTitle(formData.seo_title);

if (seoTitleError !== true) {
  showSwal("Validation Error!", seoTitleError, "warning");
  return;
}

const seoDescriptionError = validateSeoDescription(
  formData.seo_description
);

if (seoDescriptionError !== true) {
  showSwal("Validation Error!", seoDescriptionError, "warning");
  return;
}

const seoKeywordsError = validateSeoKeywords(
  formData.seo_keywords
);

if (seoKeywordsError !== true) {
  showSwal("Validation Error!", seoKeywordsError, "warning");
  return;
}

formData.name = formData.name.trim();
formData.seo_title = formData.seo_title.trim();
formData.seo_description = formData.seo_description.trim();
formData.seo_keywords = formData.seo_keywords.trim();

formData.slug = generateSlug(formData.name);

      if (hasDuplicate(formData.name, formData.slug, editingItem?.id)) {
        showSwal(
          "Duplicate Found!",
          "A category with the same name already exists.",
          "warning"
        );
        return;
      }

      const dataToSend = {};

      allowedFields.forEach((field) => {
        if (field === "cat_image") {
          if (formData.cat_image instanceof File) {
            dataToSend.cat_image = formData.cat_image;
          }
        } else {
          dataToSend[field] = formData[field];
        }
      });
      dataToSend.slug = formData.slug;

      try {
        if (editingItem) {
          await updateItem(editingItem.id, dataToSend);
          showSwal("Updated!", "Category updated successfully.", "success");
        } else {
          await createItem(dataToSend);
          showSwal("Created!", "Category created successfully.", "success");
        }

        setModalOpen(false);
        setEditingItem(null);
      } catch (error) {
        showSwal("Error!", error.message || "Something went wrong.", "error");
      }
    },
    [allowedFields, editingItem, updateItem, createItem, hasDuplicate, showSwal]
  );

  const openModal = useCallback((item = null) => {
    setEditingItem(item);
    setModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setModalOpen(false);
    setEditingItem(null);
  }, []);

  const handleDelete = useCallback(
    (id) => {
      Swal.fire({
        title: "Are you sure?",
        text: "This action cannot be undone!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, delete it!",
        cancelButtonText: "Cancel",
        customClass: {
          confirmButton: "btn btn-primary m-1",
          cancelButton: "btn btn-danger m-1",
        },
        buttonsStyling: false,
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            await deleteItem(id);
            showSwal("Deleted!", "Category deleted.", "success");
          } catch (error) {
            Swal.fire("Error!", error.message || "Delete failed.", "error");
          }
        }
      });
    },
    [deleteItem]
  );

  const handleToggleStatus = useCallback(
    async (item) => {
      try {
        const updatedData = {
          name: item.name,
          slug: item.slug,
          status: item.status ? 0 : 1,
        };

        await updateItem(item.id, updatedData);
        showSwal("Success!", "Status updated.", "success");
      } catch {
        showSwal("Error!", "Failed to update status.", "error");
      }
    },
    [updateItem, showSwal]
  );

  const tableData = useMemo(
    () => categories.map((item, idx) => ({ ...item, sl: idx + 1 })),
    [categories]
  );
useEffect(() => {
  const style = document.createElement("style");
  style.id = "category-swal-zindex";

  style.innerHTML = `
    .swal2-container {
      z-index: 20000 !important;
    }

    .category-swal-popup {
      z-index: 20001 !important;
    }
  `;

  document.head.appendChild(style);

  return () => {
    const existingStyle = document.getElementById("category-swal-zindex");
    if (existingStyle) {
      existingStyle.remove();
    }
  };
}, []);
  return (
    <DashboardWrapper>
      <div className="container-fluid mt-4">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <button
            className="btn btn-secondary"
            onClick={() => navigate(-1)}
          >
            Back
          </button>

          <button
            type="button"
            className="btn btn-primary"
            onClick={() => openModal()}
          >
            Create Category
          </button>
        </div>

        <div className="card shadow">
          <div className="card-header">
            <h4 className="mb-0">Categories</h4>
          </div>

          {modalOpen && (
            <div
              className="modal fade show"
              style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
            >
              <div className="modal-dialog modal-dialog-centered ">
                <div className="modal-content">

                  <div className="modal-header">
                    <h5 className="modal-title px-5">
                      {editingItem ? "Edit Category" : "Create Category"}
                    </h5>

                    <button
                      className="btn-close"
                      onClick={closeModal}
                    />
                  </div>

                  <div className="modal-body px-5 py-4">
                    <CrudForm
                      initialData={getFormData(editingItem)}
                      onSubmit={handleSubmit}
                      allowedFields={allowedFields}
                      fileFields={[
                        { name: "cat_image", multiple: false }
                      ]}
                    />
                  </div>

                </div>
              </div>
            </div>
          )}

          {loading ? (
            <div className="text-center p-5">
              <div className="spinner-border text-primary" />
            </div>
          ) : (
            <div className="card-body">
              <CrudTable
                data={tableData}
                onEdit={openModal}
                onDelete={handleDelete}
                onToggleStatus={handleToggleStatus}
                columns={["sl", "id", "cat_image", "name", "status"]}
              />
            </div>
          )}
        </div>
      </div>
    </DashboardWrapper>
  );
}